/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { ROUTES } from '../../constants/routes.js';
import Heading from '../../atoms/Heading/Heading';
import SideBanner from '../SideBanner/SideBanner';
import LegacySideBanner from '../LegacySideBanner/LegacySideBanner';
import leftSideImage from '../../../assets/images/p-plus-learn.png';
import Testimonial from '../../../assets/images/testimonial.png';
import SignInForm from '../SignInForm/SignInForm';
import { getConsentPoliciesFromAirdropByName, event } from '../../../services';
import { AirDropContext } from '../../../utils/AirDropProvider';
import * as Constants from '../../constants';
import useFeatures from '../../hooks/useFeatures';
import useTransitionalNavigate from '../../hooks/useTransitionalNavigate';
import { SIGNIN_CATEGORY, SIGNIN_NAME } from '../../constants/googleAnalytics4Event';
import '../../../styles/e2e/e2e-comms.scss';
import CreateAccountBlurb from '../../atoms/CreateAccountBlurb/CreateAccountBlurb';
import useTranslation from '../../hooks/useTranslation';
import { setPageTitle } from '../../../utils/commonUtils.js';

function SignIn() {
  const config: any = useContext(AirDropContext);
  const gaEventName = SIGNIN_NAME;
  const gaEventCategory = SIGNIN_CATEGORY;
  const navigate = useTransitionalNavigate();
  const { translateText, lang } = useTranslation();
  const { search } = useLocation();
  const [isE2EScriptLoaded, setIsE2EScriptLoaded] = useState<boolean>(false);
  const fromWebChallenge = useMatch({ path: `${config?.URL_PREFIX}/webchallenge`, caseSensitive: true, end: false });
  const { signupConfig } = config;
  const shouldShowCreateAccountBlurb = signupConfig?.show;

  useEffect(() => {
    if (fromWebChallenge) {
      navigate({ pathname: `${ROUTES.LOGIN_INTERNAL}`, search }, { replace: true });
    }
    event(gaEventName, gaEventCategory, 'Render', `${gaEventCategory} render`);
  }, []);

  useEffect(() => {
    setPageTitle(translateText(Constants.SIGNIN_TITLE), config.logoTreatment);
  }, [lang]);

  const { isChannels, isTurbo, isPDotCom, isLegacy } = useFeatures();

  let heading;
  let textField;
  if (isChannels) {
    heading = translateText('Welcome to Study Prep', 'values/signin/header/greetingLine1');
    textField = translateText('Discover how we help students get better grades.', 'signin/header/greetingLine2');
  } else if (isTurbo) {
    heading = translateText('Welcome to Pearson+', 'values/signin/header/greetingLine1');
    // eslint-disable-next-line max-len
    textField = translateText('Sign in to access your eTextbook and study prep tools. Your ultimate study session is one click away.', 'signin/header/greetingLine2');
  } else if (isPDotCom) {
    heading = translateText('Welcome', 'values/signin/header/greetingLine1');
    textField = translateText('Sign in and let the learning begin!', 'values/signin/header/greetingLine2');
  }

  const tou: string = getConsentPoliciesFromAirdropByName(config, 'Terms of Use');
  const privacyPolicy: string = getConsentPoliciesFromAirdropByName(config, 'Privacy Policy');

  useEffect(() => {
    // Check if all script Loaded for SignIn Route
    const scriptLoadedHandler = () => {
      setIsE2EScriptLoaded(true);
    };
    window.addEventListener('load', scriptLoadedHandler);
    return () => {
      window.removeEventListener('load', scriptLoadedHandler);
    };
  }, []);

  useEffect(() => {
    if (!isE2EScriptLoaded) return;
    const wrapper = document.getElementById('e2e-comms-wrapper');
    try {
      window._e2e_comms?.add(wrapper);
      // eslint-disable-next-line consistent-return
      return () => {
        window._e2e_comms?.remove(wrapper);
      };
    } catch (e) {
      console.log(e);
    }
  }, [isE2EScriptLoaded]);

  let sideBannerCustomClass = '';
  if (isChannels) {
    sideBannerCustomClass = 'testimonial';
  } else if (isTurbo) {
    sideBannerCustomClass = 'mlr-auto';
  }

  return (
    <div className="page-container">
      {isLegacy ? (
        <LegacySideBanner page="signin" />
      ) : (
        <SideBanner
          leftSideImage={isChannels ? Testimonial : leftSideImage}
          heading={heading}
          textField={textField}
          page={isTurbo ? 'signin-pplus' : 'signin'}
          customClass={sideBannerCustomClass}
          imagePlaceholderClassName={isChannels ? 'channels-image-loading-placeholder' : 'login-image-loading-placeholder'}
        />
      )}
      <div className="right-side">
        <div className="form-container">
          <Heading Tag="h1" addClasses="heading">
            {translateText('Sign in')}
          </Heading>

          {shouldShowCreateAccountBlurb && <CreateAccountBlurb gaEventCategory={gaEventCategory} />}

          <SignInForm />

          <p className="consent-blurb" id="agreeTermsPrivacy">
            {isPDotCom || isTurbo || isLegacy ? (
              <Trans i18nKey="signInConsentsPoliciesPDotCom">
                {'By signing in, you agree to our '}
                <a href={tou || 'https://pearson.com'} target="_blank" rel="noreferrer">
                  Terms of use
                </a>
                {' and acknowledge our '}
                <a href={privacyPolicy || 'https://pearson.com'} target="_blank" rel="noreferrer">
                  Privacy notice
                </a>
                .
              </Trans>
            ) : (
              <Trans i18nKey="signInConsentsPolicies">
                {'By clicking "Sign in", you agree to our '}
                <a href={tou || 'https://pearson.com'} target="_blank" rel="noreferrer">
                  Terms of use
                </a>
                {' and acknowledge our '}
                <a href={privacyPolicy || 'https://pearson.com'} target="_blank" rel="noreferrer">
                  Privacy notice
                </a>
                .
              </Trans>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignIn;
